import React, { useState } from 'react';
import { getApiUrl, getDefaultHeaders, apiConfig } from '../../lib/api';

export const NetworkDiagnostics: React.FC = () => {
  const [results, setResults] = useState<any[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const addResult = (test: string, status: 'success' | 'error' | 'info', data: any) => {
    setResults(prev => [...prev, { 
      test, 
      status, 
      data, 
      timestamp: new Date().toISOString() 
    }]);
  };

  const runDiagnostics = async () => {
    setIsRunning(true);
    setResults([]);

    // Test 1: Environment Variables
    addResult('Environment Variables', 'info', {
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      REACT_APP_WS_URL: process.env.REACT_APP_WS_URL,
      REACT_APP_ENV: process.env.REACT_APP_ENV,
      apiConfig
    });

    // Test 2: Basic Connectivity
    try {
      const url = getApiUrl('/health');
      addResult('Health Check URL', 'info', { url });

      const response = await fetch(url, {
        method: 'GET',
        headers: getDefaultHeaders()
      });

      addResult('Health Check Response', response.ok ? 'success' : 'error', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        url: response.url
      });

      if (response.ok) {
        const data = await response.json();
        addResult('Health Check Data', 'success', data);
      } else {
        const errorText = await response.text();
        addResult('Health Check Error', 'error', errorText);
      }
    } catch (error) {
      addResult('Health Check Failed', 'error', {
        message: (error as Error).message,
        name: (error as Error).name
      });
    }

    // Test 3: CORS Preflight
    try {
      const url = getApiUrl('/api/games');
      const response = await fetch(url, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'Content-Type'
        }
      });

      addResult('CORS Preflight', response.ok ? 'success' : 'error', {
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
        origin: window.location.origin
      });
    } catch (error) {
      addResult('CORS Preflight Failed', 'error', {
        message: (error as Error).message,
        origin: window.location.origin
      });
    }

    // Test 4: Games Endpoint
    try {
      const url = getApiUrl('/api/games');
      const response = await fetch(url);
      
      addResult('Games Endpoint', response.ok ? 'success' : 'error', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        const data = await response.json();
        addResult('Games Data', 'success', {
          gameCount: data.games?.length || 0,
          hasGames: !!data.games
        });
      }
    } catch (error) {
      addResult('Games Endpoint Failed', 'error', (error as Error).message);
    }

    setIsRunning(false);
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '20px auto', 
      padding: '20px', 
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontFamily: 'monospace',
      fontSize: '14px'
    }}>
      <h2>🔧 Network Diagnostics</h2>
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={runDiagnostics}
          disabled={isRunning}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          {isRunning ? 'Running...' : 'Run Diagnostics'}
        </button>
        <button 
          onClick={clearResults}
          style={{
            padding: '10px 20px',
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear
        </button>
      </div>

      {results.map((result, index) => (
        <div 
          key={index}
          style={{
            marginBottom: '15px',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: 
              result.status === 'success' ? '#d4edda' : 
              result.status === 'error' ? '#f8d7da' : '#d1ecf1'
          }}
        >
          <div style={{ 
            fontWeight: 'bold', 
            marginBottom: '5px',
            color: 
              result.status === 'success' ? '#155724' : 
              result.status === 'error' ? '#721c24' : '#0c5460'
          }}>
            {result.status === 'success' ? '✅' : result.status === 'error' ? '❌' : 'ℹ️'} {result.test}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
            {result.timestamp}
          </div>
          <pre style={{ 
            margin: 0, 
            whiteSpace: 'pre-wrap', 
            fontSize: '12px',
            backgroundColor: '#f8f9fa',
            padding: '8px',
            borderRadius: '3px'
          }}>
            {JSON.stringify(result.data, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
};